import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import {
  CheckCircle2, Calendar, MapPin, CreditCard, ShieldCheck, FileVideo,
  Copy, Loader2, AlertTriangle, Clock, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/** BookingShare — the public status page a customer visits via their
 *  share_token link. Emphasises the *current* state:
 *   - pending_approval  → amber "waiting" banner
 *   - confirmed         → lime "approved ✓" banner
 *   - denied            → red "declined" banner + reason
 *   - active/completed  → normal confirmation view */
export default function BookingShare() {
  const { bookingId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setError("Missing share token"); return; }
    api.get(`/public/booking/${bookingId}`, { params: { token } })
      .then((r) => setBooking(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Confirmation not found"));
  }, [bookingId, token]);

  // Auto-refresh every 30s while pending_approval so the customer sees the
  // decision as soon as the manager acts.
  useEffect(() => {
    if (!booking) return;
    if (booking.status !== "pending_approval") return;
    const iv = setInterval(() => {
      api.get(`/public/booking/${bookingId}`, { params: { token } })
        .then((r) => setBooking(r.data))
        .catch(() => {});
    }, 30000);
    return () => clearInterval(iv);
  }, [booking, bookingId, token]);

  const copyLink = () => {
    navigator.clipboard?.writeText(window.location.href).then(
      () => toast.success("Link copied"),
      () => toast.error("Copy failed")
    );
  };

  if (error) {
    return (
      <section className="mx-auto max-w-xl px-5 py-16 text-center">
        <div className="glass rounded-3xl p-8 space-y-3">
          <AlertTriangle size={28} className="text-red-400 mx-auto" />
          <h1 className="font-serif text-2xl">Confirmation not available</h1>
          <p className="text-white/60 text-sm">{error}</p>
          <Link to="/fleet" className="btn-gold inline-flex px-5 py-2.5 rounded-full text-xs mt-2">
            Back to the fleet
          </Link>
        </div>
      </section>
    );
  }

  if (!booking) {
    return (
      <div className="h-96 flex items-center justify-center">
        <Loader2 size={22} className="animate-spin text-gold" />
      </div>
    );
  }

  const isRental = booking.type === "rental";
  const isPending = booking.status === "pending_approval";
  const isDenied  = booking.status === "denied";
  const isApproved = booking.status === "confirmed" || booking.status === "active";

  return (
    <section data-testid="booking-share-page" className="mx-auto max-w-3xl px-5 sm:px-8 py-12 space-y-6">
      <StatusBanner
        booking={booking}
        isPending={isPending}
        isDenied={isDenied}
        isApproved={isApproved}
      />

      <div className="glass rounded-3xl p-6 sm:p-8 space-y-4">
        <div className="text-center">
          <div className="label-caps text-electric text-xs">
            {isPending ? "Reservation pending review" :
             isDenied ? "Reservation not approved" :
             "Reservation confirmation"}
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl mt-1">
            {isRental ? "Your self-drive rental" : "Your ride"}
          </h1>
          <div className="text-white/60 text-sm mt-1">{booking.vehicle_name}</div>
        </div>

        <div className="rounded-2xl bg-ink-950/60 border border-white/8 p-4 sm:p-5 space-y-2 text-sm">
          <div className="flex items-center justify-between gap-4">
            <span className="text-white/60">Confirmation #</span>
            <span className="text-gold font-mono text-xs sm:text-sm">{booking.id}</span>
          </div>
          {isRental ? (
            <>
              <Row icon={Calendar} k="Pickup" v={new Date(booking.start_at).toLocaleString()} />
              <Row icon={Calendar} k="Return" v={new Date(booking.end_at).toLocaleString()} />
              <Row icon={MapPin} k="Pickup at" v={booking.pickup_location} />
              <Row icon={MapPin} k="Return to" v={booking.return_location} />
              <Row k="Days" v={`${booking.days}`} />
              {booking.daily_mileage_limit != null && (
                <Row k="Mileage / day" v={`${booking.daily_mileage_limit} mi`} />
              )}
            </>
          ) : (
            <>
              <Row icon={Calendar} k="Pickup" v={new Date(booking.pickup_datetime).toLocaleString()} />
              <Row icon={MapPin} k="From" v={booking.pickup} />
              <Row icon={MapPin} k="To" v={booking.dropoff} />
              <Row k="Passengers" v={booking.passenger_count} />
              <Row k="Duration" v={`${booking.estimated_duration_hours} hrs`} />
            </>
          )}
          <div className="border-t border-white/10 pt-2 mt-2" />
          <Row icon={CreditCard} k="Total"
            v={<span className="text-gold text-base">${booking.amount?.toFixed(2)}</span>} />
          {isRental && booking.deposit != null && (
            <Row k="Refundable deposit" v={`$${booking.deposit.toFixed(2)}`} />
          )}
        </div>

        {isRental && isApproved && (
          <div className="rounded-2xl border border-electric/25 bg-electric/5 p-4 text-sm">
            {booking.inspection_video_url ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-electric">
                  <ShieldCheck size={14} />
                  <span className="label-caps text-[10px]">Contract finalized · inspection video attached</span>
                </div>
                <div className="rounded-xl overflow-hidden bg-ink-950 border border-white/10">
                  <video
                    data-testid="booking-share-video"
                    src={booking.inspection_video_url}
                    controls preload="metadata"
                    className="w-full max-h-72 bg-black"
                  />
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2 text-white/75">
                <FileVideo size={14} className="text-electric mt-0.5 shrink-0" />
                <span>Your rental agreement is <span className="text-white">reserved</span>. Once our team completes the walk-around inspection video at pickup, the finalized contract package will be attached here and emailed to <span className="text-white">{booking.customer_email}</span>.</span>
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          data-testid="booking-share-copy"
          onClick={copyLink}
          className="w-full py-2.5 rounded-full text-xs label-caps border border-white/12 hover:border-gold text-white/70 hover:text-white inline-flex items-center justify-center gap-2"
        >
          <Copy size={12} /> Copy status link
        </button>

        <p className="text-[11px] text-white/40 text-center leading-relaxed">
          Bookmark this page — you can revisit it anytime to see your status, contract,
          and (once released) the pre-rental inspection video.
        </p>
      </div>
    </section>
  );
}

function StatusBanner({ booking, isPending, isDenied, isApproved }) {
  if (isPending) {
    return (
      <div
        data-testid="booking-share-status-pending"
        className="rounded-3xl border border-amber-400/40 bg-amber-400/8 p-6 sm:p-8 text-center space-y-3"
      >
        <div className="mx-auto w-16 h-16 rounded-full bg-amber-400/15 border border-amber-400/40 flex items-center justify-center relative">
          <Clock size={32} className="text-amber-300" />
          <span className="absolute inset-0 rounded-full border-2 border-amber-400/40 animate-ping" />
        </div>
        <div className="label-caps text-amber-300 text-xs">Awaiting manager approval</div>
        <h2 className="font-serif text-2xl sm:text-3xl">We're reviewing your rental</h2>
        <p className="text-white/70 text-sm max-w-lg mx-auto">
          Your signed contract is on the manager's desk. You'll get an email and text
          the moment it's approved — usually within a few hours. This page auto-refreshes.
        </p>
      </div>
    );
  }
  if (isDenied) {
    return (
      <div
        data-testid="booking-share-status-denied"
        className="rounded-3xl border border-red-500/40 bg-red-500/8 p-6 sm:p-8 text-center space-y-3"
      >
        <div className="mx-auto w-16 h-16 rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center">
          <XCircle size={32} className="text-red-300" />
        </div>
        <div className="label-caps text-red-300 text-xs">Reservation declined</div>
        <h2 className="font-serif text-2xl sm:text-3xl">We're unable to approve this rental</h2>
        {booking.denied_reason && (
          <div className="mx-auto max-w-lg rounded-2xl bg-ink-950/60 border border-red-500/25 px-4 py-3 text-sm text-white/85 text-left">
            <div className="label-caps text-[9px] text-red-300 mb-1">Reason</div>
            {booking.denied_reason}
          </div>
        )}
        <p className="text-white/60 text-xs">
          Any deposit hold has been released. Reply to your confirmation email if you have questions.
        </p>
      </div>
    );
  }
  if (isApproved) {
    return (
      <div
        data-testid="booking-share-status-approved"
        className="rounded-3xl border border-lime-400/45 bg-gradient-to-br from-lime-400/10 to-cyan/8 p-6 sm:p-8 text-center space-y-3"
      >
        <div className="mx-auto w-16 h-16 rounded-full bg-lime-400/20 border border-lime-400/50 flex items-center justify-center">
          <CheckCircle2 size={32} className="text-lime-300" />
        </div>
        <div className="label-caps text-lime-300 text-xs">Reservation approved</div>
        <h2 className="font-serif text-2xl sm:text-3xl">You're all set</h2>
        <p className="text-white/70 text-sm max-w-lg mx-auto">
          Your rental is confirmed. See pickup and contract details below.
        </p>
      </div>
    );
  }
  // Neutral fallback for other states (cancelled/expired/etc.)
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8 text-center">
      <div className="label-caps text-white/60 text-xs">Status</div>
      <h2 className="font-serif text-2xl mt-1">{(booking.status || "unknown").replace(/_/g, " ")}</h2>
    </div>
  );
}

function Row({ icon: Icon, k, v }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-white/60 inline-flex items-center gap-1.5">
        {Icon && <Icon size={12} className="text-electric" />}{k}
      </span>
      <span className="text-white text-right">{v}</span>
    </div>
  );
}
